import React, { useState } from 'react';
import { SEO } from '../components/SEO';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ArrowRightIcon, MailIcon, ShieldIcon, CheckCircleIcon, SparklesIcon, UsersIcon, SendIcon, LockIcon, BotIcon, StarIcon, CrownIcon, BriefcaseIcon, FileTextIcon, ReceiptIcon, TrendingUpIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type PackageId = 'starter' | 'standard' | 'premium' | 'google';
type EmailPackage = {
  id: PackageId;
  name: string;
  shortName: string;
  tagline: string;
  price: string;
  unit: string;
  storage: string;
  description: string;
  features: string[];
  hasKodee: boolean;
  badge?: string;
  icon: React.ReactNode;
};
const emailPackages: EmailPackage[] = [{
  id: 'starter',
  name: 'Starter Business Email',
  shortName: 'Starter',
  tagline: 'Voor zzp en freelancers die professioneel willen mailen',
  price: '€3,99',
  unit: 'per mailbox per maand',
  storage: '5 GB',
  description: 'Een professioneel mailadres op eigen domein, met Kodee AI inbegrepen. Geschikt als startpunt voor zelfstandige adviseurs en kleine kantoren.',
  features: ['5 GB opslag per mailbox', '5 doorstuurregels en 5 aliassen', 'Werkt met Outlook, Apple Mail, Gmail-app en elke andere mailapp', 'Geavanceerde bescherming tegen spam, malware en phishing', 'SPF, DKIM, DMARC en ARC standaard ingesteld bij oplevering', 'Versleutelde opslag en geen Hostinger-handtekening', 'Gratis domein bij eerste jaar'],
  hasKodee: true,
  icon: <MailIcon className="h-5 w-5" data-id="element-1190" />
}, {
  id: 'standard',
  name: 'Standard Business Email',
  shortName: 'Standard',
  tagline: 'Voor kleine teams die meer ruimte en meer aliassen willen',
  price: '€4,99',
  unit: 'per mailbox per maand',
  storage: '20 GB',
  description: 'De middelste optie. Ruim genoeg voor adviseurs die regelmatig bijlagen versturen en meerdere aliassen willen voor verschillende rollen of diensten.',
  features: ['20 GB opslag per mailbox', '20 doorstuurregels en 10 aliassen', 'Werkt met Outlook, Apple Mail, Gmail-app en elke andere mailapp', 'Geavanceerde bescherming tegen spam, malware en phishing', 'SPF, DKIM, DMARC en ARC standaard ingesteld bij oplevering', 'Versleutelde opslag en geen Hostinger-handtekening', 'Gratis domein bij eerste jaar'],
  hasKodee: true,
  badge: 'Aanrader',
  icon: <SparklesIcon className="h-5 w-5" data-id="element-1191" />
}, {
  id: 'premium',
  name: 'Premium Business Email',
  shortName: 'Premium',
  tagline: 'Voor groeiende teams die maximale ruimte en aliassen nodig hebben',
  price: '€6,99',
  unit: 'per mailbox per maand',
  storage: '50 GB',
  description: 'Voor kantoren waar veel mail binnenkomt en uitgaat. Maximale opslag, maximale aliassen en alle AI-tools.',
  features: ['50 GB opslag per mailbox', '50 doorstuurregels en 30 aliassen', 'Werkt met Outlook, Apple Mail, Gmail-app en elke andere mailapp', 'Geavanceerde bescherming tegen spam, malware en phishing', 'SPF, DKIM, DMARC en ARC standaard ingesteld bij oplevering', 'Versleutelde opslag en geen Hostinger-handtekening', 'Gratis domein bij eerste jaar'],
  hasKodee: true,
  icon: <CrownIcon className="h-5 w-5" data-id="element-1192" />
}, {
  id: 'google',
  name: 'Google Workspace Starter',
  shortName: 'Google Workspace',
  tagline: 'Voor teams die volledig in het Google-ecosysteem werken',
  price: '€10,99',
  unit: 'per gebruiker per maand',
  storage: '30 GB',
  description: 'E-mail op eigen domein, maar dan in de bekende Google-omgeving. Inclusief Gmail, Drive, Agenda, Meet, Docs, Sheets en Slides.',
  features: ['30 GB opslag per gebruiker', 'Gmail op eigen domein met geavanceerde phishing- en malware-bescherming', 'Google Agenda, Drive, Meet, Docs, Sheets en Slides', 'Chat-teamberichten en AppSheet', 'Videoconferenties tot 100 deelnemers', 'Centraal beheer via admin console', 'Wij regelen de DNS, domeinverificatie en oplevering'],
  hasKodee: false,
  icon: <UsersIcon className="h-5 w-5" data-id="element-1193" />
}];
type ReachTier = {
  id: string;
  range: string;
  contacts: string;
  emails: string;
  aiCredits: string;
  price: string;
};
const reachTiers: ReachTier[] = [{
  id: 'tier-1',
  range: '0 tot 500 contacten',
  contacts: '500 contacten per maand',
  emails: '3.500 e-mails per maand',
  aiCredits: '10 AI-credits per maand',
  price: '€6,99'
}, {
  id: 'tier-2',
  range: '501 tot 1.000 contacten',
  contacts: '1.000 contacten per maand',
  emails: '7.000 e-mails per maand',
  aiCredits: '20 AI-credits per maand',
  price: '€12,99'
}, {
  id: 'tier-3',
  range: '1.001 tot 2.500 contacten',
  contacts: '2.500 contacten per maand',
  emails: '17.500 e-mails per maand',
  aiCredits: '30 AI-credits per maand',
  price: '€27,99'
}, {
  id: 'tier-4',
  range: '2.501 tot 5.000 contacten',
  contacts: '5.000 contacten per maand',
  emails: '35.000 e-mails per maand',
  aiCredits: '30 AI-credits per maand',
  price: '€50,99'
}, {
  id: 'tier-5',
  range: '5.001 tot 10.000 contacten',
  contacts: '10.000 contacten per maand',
  emails: '70.000 e-mails per maand',
  aiCredits: '30 AI-credits per maand',
  price: '€80,99'
}, {
  id: 'tier-6',
  range: '10.001 tot 50.000 contacten',
  contacts: '50.000 contacten per maand',
  emails: '350.000 e-mails per maand',
  aiCredits: '30 AI-credits per maand',
  price: '€270,99'
}];
export const EmailPage: React.FC = () => {
  const [selectedPackage, setSelectedPackage] = useState<PackageId>('standard');
  const [selectedReach, setSelectedReach] = useState<string>('tier-1');
  const activePackage = emailPackages.find(p => p.id === selectedPackage) ?? emailPackages[1];
  const activeReach = reachTiers.find(t => t.id === selectedReach) ?? reachTiers[0];
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-1194">
      <SEO title="Zakelijke e-mail hosting met pakket-keuze en Kodee AI | Noord Digital" description="Vergelijk Starter, Standard, Premium Business Email en Google Workspace. Inclusief Kodee AI, agentic mail en email marketing via Hostinger Reach. Vaste maandprijs, jaarlijks vastgelegd." keywords="zakelijke e-mail hosting, business email starter, business email standard, business email premium, kodee ai, google workspace, eigen domein e-mail, email marketing, hostinger reach" canonical="https://noorddigital.nl/zakelijke-email-hosting" data-id="element-1195" />
      <Header data-id="element-1196" />

      {/* Hero */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] py-16 md:py-20" data-id="element-1197">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1198">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center" data-id="element-1199">
            <div className="lg:col-span-7" data-id="element-1200">
              <h1 className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-1201">
                Zakelijke e-mail met de pakket-keuze die bij je werk past
              </h1>
              <p className="text-white text-opacity-90 mb-8 text-lg leading-relaxed" data-id="element-1202">
                Vier pakketten, allemaal met e-mail op je eigen domein. Bij elk
                Business-pakket krijg je Kodee AI inbegrepen, de assistent die
                helpt met opstellen, samenvatten en doorzoeken. Of kies Google
                Workspace als je liever in de Google-omgeving werkt. Wij regelen
                de DNS, beveiliging en oplevering, jij krijgt een werkende
                mailbox op de afgesproken dag.
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-1203">
                <a href="#email-pakketten" className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-lg" data-id="element-1204">
                  Vergelijk de pakketten
                  <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-1205" />
                </a>
                <a href="#mailbox-advies" className="inline-flex items-center px-6 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg" data-id="element-1206">
                  Ons mailbox-advies
                  <ArrowRightIcon className="h-5 w-5 ml-2" data-id="element-1207" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5" data-id="element-1208">
              <div className="bg-white rounded-2xl p-8 shadow-2xl" data-id="element-1209">
                <div className="text-center mb-6" data-id="element-1210">
                  <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-full mb-4 p-3" data-id="element-1211">
                    <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden mascotte, zakelijke e-mail hosting" className="w-full h-full object-cover rounded-full" data-id="element-1212" />
                  </div>
                  <h3 className="font-bold text-[#111827] text-2xl mb-2" data-id="element-1213">
                    Norden bewaakt jouw mailverkeer
                  </h3>
                  <p className="text-[#6B7280]" data-id="element-1214">
                    Aflevergarantie, anti-phishing en geen rare
                    provider-branding onderaan je mail
                  </p>
                </div>
                <div className="space-y-3 pt-6 border-t border-gray-200" data-id="element-1215">
                  <div className="flex items-center gap-3" data-id="element-1216">
                    <ShieldIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1217" />
                    <span className="text-sm text-[#6B7280]" data-id="element-1218">
                      Geavanceerde beveiliging (SPF, DKIM, DMARC, ARC)
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1219">
                    <BotIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1220" />
                    <span className="text-sm text-[#6B7280]" data-id="element-1221">
                      Kodee AI inbegrepen bij alle Business-pakketten
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1222">
                    <LockIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1223" />
                    <span className="text-sm text-[#6B7280]" data-id="element-1224">
                      Spamfilter, virusbescherming en aflevergarantie
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pakket-keuze met toggle */}
      <div id="email-pakketten" className="w-full bg-white py-20 md:py-24" data-id="element-1225">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1226">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1227">
            <h2 className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-1228">
              Kies het pakket dat bij je werk past
            </h2>
            <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-1229">
              Vier pakketten met e-mail op je eigen domein. Bij elk
              Business-pakket zit Kodee AI inbegrepen. Tik op een pakket om de
              prijs, opslag en features te zien.
            </p>
          </div>

          {/* Toggle */}
          <div className="max-w-4xl mx-auto mb-10" data-id="element-1230">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-2 bg-[#F7F8FA] rounded-2xl" data-id="element-1231">
              {emailPackages.map(pkg => {
              const isActive = pkg.id === selectedPackage;
              return <button key={pkg.id} onClick={() => setSelectedPackage(pkg.id)} className={`flex items-center justify-center gap-2 px-3 py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${isActive ? 'bg-white text-[#007AFF] shadow-md' : 'bg-transparent text-[#6B7280] hover:text-[#111827]'}`} data-id="element-1232">
                    {pkg.icon}
                    <span data-id="element-1233">{pkg.shortName}</span>
                    {pkg.badge && isActive && <span className="ml-1 hidden lg:inline-flex items-center gap-1 bg-[#007AFF] text-white text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full" data-id="element-1234">
                        <StarIcon className="h-3 w-3" data-id="element-1235" />
                        {pkg.badge}
                      </span>}
                  </button>;
            })}
            </div>
          </div>

          {/* Pakket card (dynamic) */}
          <AnimatePresence mode="wait" data-id="element-1236">
            <motion.div key={activePackage.id} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.25
          }} className="bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] rounded-2xl p-8 md:p-12 max-w-5xl mx-auto shadow-sm border border-gray-100" data-id="element-1237">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start" data-id="element-1238">
                {/* Prijs blok */}
                <div className="md:col-span-5" data-id="element-1239">
                  <div className="bg-white rounded-2xl p-6 md:p-8 shadow-md" data-id="element-1240">
                    <div className="inline-flex items-center gap-2 bg-[#007AFF] bg-opacity-10 text-[#007AFF] text-xs font-semibold px-3 py-1 rounded-full mb-4" data-id="element-1241">
                      {activePackage.icon}
                      <span data-id="element-1242">{activePackage.name}</span>
                    </div>
                    <p className="text-[#6B7280] text-sm mb-4 leading-relaxed" data-id="element-1243">
                      {activePackage.tagline}
                    </p>
                    <div className="flex items-baseline gap-2 mb-2" data-id="element-1244">
                      <span className="text-5xl font-bold text-[#111827]" data-id="element-1245">
                        {activePackage.price}
                      </span>
                    </div>
                    <p className="text-[#6B7280] text-sm mb-4" data-id="element-1246">
                      {activePackage.unit}
                    </p>
                    <div className="border-t border-gray-200 pt-4 mb-6" data-id="element-1247">
                      <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-1" data-id="element-1248">
                        Opslag
                      </p>
                      <p className="text-[#111827] font-semibold" data-id="element-1249">
                        {activePackage.storage} per mailbox
                      </p>
                    </div>
                    <a href="/contact" className="inline-flex items-center justify-center w-full bg-[#007AFF] text-white font-semibold py-3 px-6 rounded-lg shadow-md" data-id="element-1250">
                      Vraag {activePackage.shortName} aan
                      <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-1251" />
                    </a>
                    <p className="text-xs text-[#6B7280] text-center mt-3" data-id="element-1252">
                      Jaarlijks vastgelegd, prijzen excl. BTW. Setup en
                      oplevering door Noord Digital.
                    </p>
                  </div>
                </div>

                {/* Inhoud blok */}
                <div className="md:col-span-7" data-id="element-1253">
                  <h3 className="font-bold text-[#111827] text-2xl mb-3" data-id="element-1254">
                    Wat zit erin?
                  </h3>
                  <p className="text-[#6B7280] leading-relaxed mb-6" data-id="element-1255">
                    {activePackage.description}
                  </p>
                  <ul className="space-y-3 mb-6" data-id="element-1256">
                    {activePackage.features.map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-1257">
                        <CheckCircleIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1258" />
                        <span className="text-[#6B7280] leading-relaxed" data-id="element-1259">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  {/* Kodee AI uitleg bij alle Business tiers */}
                  {activePackage.hasKodee && <div className="bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-2xl p-6 text-white shadow-lg" data-id="element-1260">
                      <div className="flex items-start gap-4" data-id="element-1261">
                        <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden p-1" data-id="element-1262">
                          <img src="https://cdn.magicpatterns.com/uploads/gF49Gkb49foeib4L9f3x1k/Norden_denkent.png" alt="Norden AI" className="w-full h-full object-contain" data-id="element-1263" />
                        </div>
                        <div data-id="element-1264">
                          <h4 className="font-bold text-lg mb-2" data-id="element-1265">
                            Kodee AI & ChatGPT in je inbox
                          </h4>
                          <p className="text-white text-opacity-90 leading-relaxed text-sm mb-3" data-id="element-1266">
                            Bij alle Business-pakketten krijg je Kodee, de AI
                            assistent aangedreven door ChatGPT die direct in je
                            mailbox werkt. Geen extra abonnement, geen losse
                            tabbladen.
                          </p>
                          <ul className="space-y-2 text-sm text-white text-opacity-90" data-id="element-1267">
                            <li className="flex items-start gap-2" data-id="element-1268">
                              <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="element-1269" />
                              <span data-id="element-1270">
                                Laat ChatGPT direct concepten schrijven in jouw
                                eigen toon
                              </span>
                            </li>
                            <li className="flex items-start gap-2" data-id="element-1271">
                              <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="element-1272" />
                              <span data-id="element-1273">
                                Directe AI-samenvattingen van lange threads en
                                bijlagen
                              </span>
                            </li>
                            <li className="flex items-start gap-2" data-id="element-1274">
                              <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="element-1275" />
                              <span data-id="element-1276">
                                AI-gestuurde e-mailzoekfunctie op onderwerp in
                                plaats van trefwoord
                              </span>
                            </li>
                            <li className="flex items-start gap-2" data-id="element-1277">
                              <CheckCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" data-id="element-1278" />
                              <span data-id="element-1279">
                                Agentische e-mail (bèta): laat de AI zelfstandig
                                acties oppakken
                              </span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Vergelijkings-strook */}
          <div className="max-w-5xl mx-auto mt-10 grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-1280">
            {emailPackages.map(pkg => {
            const isActive = pkg.id === selectedPackage;
            return <button key={pkg.id} onClick={() => setSelectedPackage(pkg.id)} className={`text-left p-5 rounded-xl border-2 transition-all duration-200 ${isActive ? 'border-[#007AFF] bg-white shadow-md' : 'border-transparent bg-[#F7F8FA] hover:border-gray-200'}`} data-id="element-1281">
                  <div className="flex items-center gap-2 mb-2" data-id="element-1282">
                    <div className="w-8 h-8 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF]" data-id="element-1283">
                      {pkg.icon}
                    </div>
                    <span className="font-semibold text-[#111827] text-sm leading-tight" data-id="element-1284">
                      {pkg.shortName}
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-[#111827]" data-id="element-1285">
                    {pkg.price}
                  </p>
                  <p className="text-xs text-[#6B7280] mb-2" data-id="element-1286">{pkg.unit}</p>
                  <p className="text-xs text-[#007AFF] font-semibold" data-id="element-1287">
                    {pkg.storage} opslag
                  </p>
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* 3-mailbox advies */}
      <div id="mailbox-advies" className="w-full bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] py-20 md:py-24" data-id="element-1288">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1289">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1290">
            <span className="inline-block bg-[#007AFF] bg-opacity-10 text-[#007AFF] text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full mb-4" data-id="element-1291">
              Ons advies
            </span>
            <h2 className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-1292">
              Drie mailboxen, drie functies, één strak systeem
            </h2>
            <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-1293">
              We adviseren elke adviespraktijk om de mail te splitsen over drie
              functies. Het houdt je inbox overzichtelijk en zorgt dat de juiste
              mensen de juiste mail krijgen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto" data-id="element-1294">
            {/* Info */}
            <div className="bg-white rounded-2xl p-8 shadow-md" data-id="element-1295">
              <div className="w-14 h-14 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF] mb-4" data-id="element-1296">
                <BriefcaseIcon className="h-7 w-7" data-id="element-1297" />
              </div>
              <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-1" data-id="element-1298">
                Algemene communicatie
              </p>
              <h3 className="font-bold text-[#111827] text-base lg:text-xl mb-3 font-mono break-all leading-tight" data-id="element-1299">
                info@<span className="text-[#007AFF]" data-id="element-1300">jouwbedrijf</span>.nl
              </h3>
              <p className="text-[#6B7280] text-sm leading-relaxed" data-id="element-1301">
                Voor de grote communicatie met klanten, partners en het algemene
                contact. Dit is het adres dat op je website, visitekaartje en
                briefpapier staat.
              </p>
            </div>

            {/* Administratie */}
            <div className="bg-white rounded-2xl p-8 shadow-md" data-id="element-1302">
              <div className="w-14 h-14 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF] mb-4" data-id="element-1303">
                <FileTextIcon className="h-7 w-7" data-id="element-1304" />
              </div>
              <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-1" data-id="element-1305">
                Zakelijke accounts en software
              </p>
              <h3 className="font-bold text-[#111827] text-base lg:text-xl mb-3 font-mono break-all leading-tight" data-id="element-1306">
                administratie@
                <span className="text-[#007AFF]" data-id="element-1307">jouwbedrijf</span>.nl
              </h3>
              <p className="text-[#6B7280] text-sm leading-relaxed" data-id="element-1308">
                Voor je Google-account, software-aanmeldingen en alle andere
                services waar een zakelijk account makkelijker werkt. Geen
                verwarring met je dagelijkse mail.
              </p>
            </div>

            {/* Facturen */}
            <div className="bg-white rounded-2xl p-8 shadow-md" data-id="element-1309">
              <div className="w-14 h-14 bg-[#007AFF] bg-opacity-10 rounded-full flex items-center justify-center text-[#007AFF] mb-4" data-id="element-1310">
                <ReceiptIcon className="h-7 w-7" data-id="element-1311" />
              </div>
              <p className="text-xs uppercase tracking-wide text-[#6B7280] mb-1" data-id="element-1312">
                Boekhouding en facturatie
              </p>
              <h3 className="font-bold text-[#111827] text-base lg:text-xl mb-3 font-mono break-all leading-tight" data-id="element-1313">
                facturen@<span className="text-[#007AFF]" data-id="element-1314">jouwbedrijf</span>.nl
              </h3>
              <p className="text-[#6B7280] text-sm leading-relaxed" data-id="element-1315">
                Voor alle inkomende en uitgaande facturen. Stuur dit adres door
                naar je boekhouder of administratiekantoor, en jouw eigen inbox
                blijft schoon.
              </p>
            </div>
          </div>

          <div className="max-w-3xl mx-auto mt-10 bg-white rounded-2xl p-6 text-center shadow-sm" data-id="element-1316">
            <p className="text-[#6B7280] leading-relaxed" data-id="element-1317">
              Drie mailboxen, allemaal in hetzelfde pakket. Bij{' '}
              <span className="font-semibold text-[#111827]" data-id="element-1318">
                {activePackage.shortName}
              </span>{' '}
              betaal je voor drie mailboxen{' '}
              <span className="font-bold text-[#007AFF]" data-id="element-1319">
                {(parseFloat(activePackage.price.replace('€', '').replace(',', '.')) * 3).toFixed(2).replace('.', ',')}{' '}
                euro per maand
              </span>
              , jaarlijks vastgelegd.
            </p>
          </div>
        </div>
      </div>

      {/* Email marketing via Hostinger Reach */}
      <div className="w-full bg-white py-20 md:py-24" data-id="element-1320">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1321">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-1322">
            <span className="inline-block bg-[#007AFF] bg-opacity-10 text-[#007AFF] text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-full mb-4" data-id="element-1323">
              Email marketing
            </span>
            <h2 className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-1324">
              Nieuwsbrieven en campagnes, schaalt met je bedrijf mee
            </h2>
            <p className="text-[#6B7280] text-lg leading-relaxed" data-id="element-1325">
              Naast dagelijks mailen kunnen we ook je nieuwsbrieven, campagnes
              en geautomatiseerde follow-ups voor je inrichten. Kies hieronder
              de schaal die bij jouw lijst past.
            </p>
          </div>

          {/* Reach toggle */}
          <div className="max-w-5xl mx-auto mb-8" data-id="element-1326">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 p-2 bg-[#F7F8FA] rounded-2xl" data-id="element-1327">
              {reachTiers.map(tier => {
              const isActive = tier.id === selectedReach;
              return <button key={tier.id} onClick={() => setSelectedReach(tier.id)} className={`flex flex-col items-center justify-center px-2 py-3 rounded-xl font-semibold text-xs transition-all duration-200 ${isActive ? 'bg-white text-[#007AFF] shadow-md' : 'bg-transparent text-[#6B7280] hover:text-[#111827]'}`} data-id="element-1328">
                    <span className="text-[10px] uppercase tracking-wide opacity-70 mb-1" data-id="element-1329">
                      Tot
                    </span>
                    <span className="text-sm font-bold" data-id="element-1330">
                      {tier.contacts.split(' ')[0]}
                    </span>
                  </button>;
            })}
            </div>
          </div>

          {/* Reach detail */}
          <AnimatePresence mode="wait" data-id="element-1331">
            <motion.div key={activeReach.id} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.25
          }} className="bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] rounded-2xl p-8 md:p-12 max-w-5xl mx-auto shadow-sm border border-gray-100" data-id="element-1332">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start" data-id="element-1333">
                <div className="md:col-span-5" data-id="element-1334">
                  <div className="bg-white rounded-2xl p-6 md:p-8 shadow-md" data-id="element-1335">
                    <div className="inline-flex items-center gap-2 bg-[#007AFF] bg-opacity-10 text-[#007AFF] text-xs font-semibold px-3 py-1 rounded-full mb-4" data-id="element-1336">
                      <SendIcon className="h-4 w-4" data-id="element-1337" />
                      <span data-id="element-1338">{activeReach.range}</span>
                    </div>
                    <div className="flex items-baseline gap-2 mb-2" data-id="element-1339">
                      <span className="text-5xl font-bold text-[#111827]" data-id="element-1340">
                        {activeReach.price}
                      </span>
                    </div>
                    <p className="text-[#6B7280] text-sm mb-6" data-id="element-1341">
                      per maand, jaarlijks vastgelegd
                    </p>
                    <div className="space-y-3 border-t border-gray-200 pt-4 mb-6" data-id="element-1342">
                      <div className="flex items-center gap-3" data-id="element-1343">
                        <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1344" />
                        <span className="text-sm text-[#6B7280]" data-id="element-1345">
                          {activeReach.contacts}
                        </span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-1346">
                        <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1347" />
                        <span className="text-sm text-[#6B7280]" data-id="element-1348">
                          {activeReach.emails}
                        </span>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-1349">
                        <CheckCircleIcon className="h-5 w-5 text-[#007AFF] flex-shrink-0" data-id="element-1350" />
                        <span className="text-sm text-[#6B7280]" data-id="element-1351">
                          {activeReach.aiCredits}
                        </span>
                      </div>
                    </div>
                    <a href="/contact" className="inline-flex items-center justify-center w-full bg-[#007AFF] text-white font-semibold py-3 px-6 rounded-lg shadow-md" data-id="element-1352">
                      Vraag email marketing aan
                      <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-1353" />
                    </a>
                  </div>
                </div>

                <div className="md:col-span-7" data-id="element-1354">
                  <h3 className="font-bold text-[#111827] text-2xl mb-3" data-id="element-1355">
                    Wat zit erin?
                  </h3>
                  <p className="text-[#6B7280] leading-relaxed mb-6" data-id="element-1356">
                    Alles wat je nodig hebt om campagnes te draaien die
                    meegroeien met je bedrijf. Sjablonen op je merk gezet,
                    automatisering vanuit je website, en rapportage waar je iets
                    aan hebt.
                  </p>
                  <ul className="space-y-3 mb-6" data-id="element-1357">
                    <li className="flex items-start gap-3" data-id="element-1358">
                      <SendIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1359" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1360">
                        Nieuwsbrieven plannen, met tracking per open en klik
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-1361">
                      <SparklesIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1362" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1363">
                        AI e-mail templates en onderwerpregel-generator
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-1364">
                      <BotIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1365" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1366">
                        Welkomst- en opvolg-e-mails over dagen of weken
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-1367">
                      <UsersIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1368" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1369">
                        Onbeperkte doelgroepsegmenten, handmatig of met AI
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-1370">
                      <TrendingUpIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1371" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1372">
                        Volledig eigen branding, geen Reach-logo onder je mail
                      </span>
                    </li>
                    <li className="flex items-start gap-3" data-id="element-1373">
                      <CheckCircleIcon className="h-5 w-5 text-[#007AFF] mt-0.5 flex-shrink-0" data-id="element-1374" />
                      <span className="text-[#6B7280] leading-relaxed" data-id="element-1375">
                        Sjablonen die we voor jou op merk zetten, plus
                        synchronisatie met je website
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Norden email security */}
      <div className="w-full bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] py-20 md:py-24" data-id="element-1376">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-1377">
          <div className="max-w-4xl mx-auto" data-id="element-1378">
            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm" data-id="element-1379">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-1380">
                <div className="text-center md:text-left" data-id="element-1381">
                  <div className="inline-block" data-id="element-1382">
                    <div className="w-48 h-48 bg-gradient-to-br from-[#007AFF] to-[#005FCC] rounded-full flex items-center justify-center p-4" data-id="element-1383">
                      <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden bewaakt zakelijke e-mail hosting" className="w-full h-full object-cover rounded-full" data-id="element-1384" />
                    </div>
                  </div>
                </div>
                <div data-id="element-1385">
                  <h2 className="font-bold text-[#111827] text-3xl mb-4" data-id="element-1386">
                    Hoe we je oplevering aanpakken
                  </h2>
                  <p className="text-[#6B7280] text-lg leading-relaxed mb-6" data-id="element-1387">
                    Wij doen de DNS, de SPF, DKIM en DMARC, de domeinverificatie
                    en de oplevering in Outlook of Apple Mail. Jij krijgt een
                    werkende mailbox op de afgesproken dag, met je oude mail
                    meeverhuisd als je dat wilt.
                  </p>
                  <div className="space-y-3" data-id="element-1388">
                    <div className="flex items-center gap-3" data-id="element-1389">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1390">
                        <CheckCircleIcon className="h-4 w-4 text-white" data-id="element-1391" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-1392">
                        DNS, SPF, DKIM en DMARC standaard ingericht
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-1393">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1394">
                        <CheckCircleIcon className="h-4 w-4 text-white" data-id="element-1395" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-1396">
                        Bestaande mail meeverhuisd waar mogelijk
                      </span>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-1397">
                      <div className="w-6 h-6 bg-[#007AFF] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1398">
                        <CheckCircleIcon className="h-4 w-4 text-white" data-id="element-1399" />
                      </div>
                      <span className="text-[#6B7280]" data-id="element-1400">
                        Persoonlijke uitleg over je mailapp na oplevering
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA-rij */}
      <div className="w-full bg-gradient-to-br from-[#007AFF] to-[#005FCC] py-16 md:py-20" data-id="element-1401">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] text-center" data-id="element-1402">
          <h2 className="font-bold text-white text-3xl md:text-4xl mb-4" data-id="element-1403">
            Combineer je e-mail met de rest
          </h2>
          <p className="text-white text-opacity-90 text-lg leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-1404">
            Email, hosting en website bij één partij betekent korte lijnen, nét
            als bij de rest van je dienstverlening.
          </p>
          <div className="flex flex-wrap gap-4 justify-center" data-id="element-1405">
            <a href="/hosting" className="inline-flex items-center px-6 py-3 bg-white text-[#007AFF] font-semibold rounded-lg shadow-md" data-id="element-1406">
              Bekijk hosting (€34,99 per maand)
              <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-1407" />
            </a>
            <a href="/websites" className="inline-flex items-center px-6 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-lg" data-id="element-1408">
              Website laten maken (vanaf €499)
              <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-1409" />
            </a>
          </div>
        </div>
      </div>

      <Footer data-id="element-1410" />
    </div>;
};